import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery } from 'react-query';
import { companyAPI, cameraAPI } from '../../services/api';
import { Camera, Play, Image, Calendar, ExternalLink, Download, RefreshCw, Video, Clock } from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/LoadingSpinner';
import Hls from 'hls.js';

import TimelapseAPI from '../../services/timelapseApi';

const CamerasPage = () => {
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [showLiveView, setShowLiveView] = useState(false);
  const [showPhotoModal, setShowPhotoModal] = useState(false);
  const [showTimelapseModal, setShowTimelapseModal] = useState(false);


  const { data: cameras, isLoading, error, refetch } = useQuery('company-cameras', companyAPI.getCameras, {
    retry: (failureCount, error) => {
      // Don't retry on auth errors (401/403) as they will be handled by redirecting to login
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return false;
      }
      // Retry up to 2 times for other errors
      return failureCount < 2;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    staleTime: 30000, // Consider data stale after 30 seconds
    cacheTime: 300000, // Keep in cache for 5 minutes
  });

  const handleViewLive = async (camera) => {
    try {
      const response = await cameraAPI.getCameraStream(camera.id);
      setSelectedCamera({
        ...camera,
        hls_url: response.data.hls_url,
        stream_url: response.data.stream_url, // For backward compatibility
        mediamtx_path: response.data.mediamtx_path
      });
      setShowLiveView(true);
    } catch (error) {
      console.error('Failed to get camera stream:', error);
      toast.error('Failed to get camera stream');
    }
  };

  const handleViewPhotos = (camera) => {
    setSelectedCamera(camera);
    setShowPhotoModal(true);
  };

  const handleCreateTimelapse = (camera) => {
    setSelectedCamera(camera);
    setShowTimelapseModal(true);
  };

  if (isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <LoadingSpinner className="h-64" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-800">
              Failed to load cameras: {error?.response?.data?.error || error?.message || 'Please try again.'}
            </span>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline flex items-center"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Camera Management</h1>
        <p className="text-gray-600">Manage your cameras and create timelapse videos</p>
      </div>

      {/* Camera Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-700">
              Error loading cameras: {error?.response?.data?.error || error?.message || 'Unknown error occurred'}
            </span>
          </div>
          <button
            onClick={() => refetch()}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline flex items-center"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Try again
          </button>
        </div>
      ) : cameras?.data?.length === 0 ? (
        <div className="text-center py-12">
          <Camera className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No cameras found</h3>
          <p className="text-gray-500">Get started by adding your first camera.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cameras.data.map(camera => (
            <div
              key={camera.id}
              className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">{camera.name}</h3>
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    camera.is_active 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {camera.is_active ? 'Active' : 'Inactive'}
                  </span>
                </div>
                
                {/* Photo and Thumbnail Count */}
                <div className="mb-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <Image className="h-4 w-4 mr-1 text-gray-500" />
                      <span><strong>{camera.photo_count || 0}</strong> photos</span>
                    </div>
                    <div className="flex items-center">
                      <Video className="h-4 w-4 mr-1 text-gray-500" />
                      <span><strong>{camera.thumbnail_count || 0}</strong> thumbnails</span>
                    </div>
                    {camera.last_photo && (
                      <div className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1 text-gray-500" />
                        <span>Last: {(() => {
                          try {
                            if (!camera.last_photo) return 'No photos';
                            const dateString = camera.last_photo.endsWith('Z') ? camera.last_photo : camera.last_photo + 'Z';
                            const dateObj = new Date(dateString);
                            // Create a new date using UTC values to force UTC formatting
                            const utcDate = new Date(dateObj.getUTCFullYear(), dateObj.getUTCMonth(), dateObj.getUTCDate(), 
                                                     dateObj.getUTCHours(), dateObj.getUTCMinutes(), dateObj.getUTCSeconds());
                            return format(utcDate, 'MM/dd/yyyy, HH:mm');
                          } catch (error) {
                            return 'Invalid date';
                          }
                        })()}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 w-full">
                  <button
                    onClick={() => handleViewPhotos(camera)}
                    className="btn btn-secondary flex items-center justify-center text-sm min-w-0 px-2"
                  >
                    <Image className="h-4 w-4 mr-1 flex-shrink-0" />
                    <span className="truncate">Photos</span>
                  </button>
                  <button
                    onClick={() => {
                      setSelectedCamera(camera);
                      setShowTimelapseModal(true);
                    }}
                    className="btn btn-secondary flex items-center justify-center text-sm min-w-0 px-2"
                  >
                    <Video className="h-4 w-4 mr-1 flex-shrink-0" />
                    <span className="truncate">Timelapse</span>
                  </button>
                  <button
                    onClick={() => handleViewLive(camera)}
                    className="btn btn-primary flex items-center justify-center text-sm min-w-0 px-2"
                  >
                    <Play className="h-4 w-4 mr-1 flex-shrink-0" />
                    <span className="truncate">View</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Live View Modal */}
      {showLiveView && selectedCamera && (
        <LiveViewModal
          camera={selectedCamera}
          onClose={() => {
            setShowLiveView(false);
            setSelectedCamera(null);
          }}
        />
      )}

      {/* Photo Modal */}
      {showPhotoModal && selectedCamera && (
        <CameraPhotosModal
          camera={selectedCamera}
          onClose={() => {
            setShowPhotoModal(false);
            setSelectedCamera(null);
          }}
        />
      )}

      {/* Timelapse Modal */}
      {showTimelapseModal && selectedCamera && (
        <CreateTimelapseModal
          camera={selectedCamera}
          onClose={() => {
            setShowTimelapseModal(false);
            setSelectedCamera(null);
          }}
        />
      )}
    </div>
  );
};

const LiveViewModal = ({ camera, onClose }) => {
  const [hlsUrl, setHlsUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);

  // Check if we have an HLS URL (new system) or need to handle RTSP (legacy)
  const hasHlsUrl = camera.hls_url && camera.hls_url.includes('.m3u8');
  const isRtspStream = camera.stream_url && camera.stream_url.startsWith('rtsp://');

  const handleCopyUrl = () => {
    const urlToCopy = camera.hls_url || camera.stream_url;
    navigator.clipboard.writeText(urlToCopy);
    toast.success('Stream URL copied to clipboard');
  };

  const handleOpenVlc = () => {
    // Try to open in VLC - use HLS URL if available, otherwise RTSP
    const streamUrl = camera.hls_url || camera.stream_url;
    window.open(`vlc://${streamUrl}`, '_blank');
  };

  const getHlsUrl = () => {
    // Get JWT token for authentication
    const token = localStorage.getItem('token');
    
    // Get HLS stream via nginx proxy
    const envBase = process.env.REACT_APP_HLS_BASE_URL;
    let baseUrl;
    
    if (envBase && typeof envBase === 'string') {
      const base = envBase.endsWith('/') ? envBase.slice(0, -1) : envBase;
      baseUrl = `${base}/camera_${camera.id}/index.m3u8`;
    } else {
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      const port = window.location.port ? `:${window.location.port}` : '';
      // Use nginx proxy for HLS streams
      const base = `${protocol}//${hostname}${port}/hls`;
      baseUrl = `${base}/camera_${camera.id}/index.m3u8`;
    }
    
    // Add JWT token as query parameter for authentication
    if (token) {
      const separator = baseUrl.includes('?') ? '&' : '?';
      return `${baseUrl}${separator}token=${encodeURIComponent(token)}`;
    }
    
    return baseUrl;
  };

  const checkHlsAvailability = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      if (hasHlsUrl) {
        // Use the HLS URL directly from the API response and add auth token
        let authenticatedUrl = camera.hls_url;
        if (token) {
          const separator = authenticatedUrl.includes('?') ? '&' : '?';
          authenticatedUrl = `${authenticatedUrl}${separator}token=${encodeURIComponent(token)}`;
        }
        console.log(`🎥 Using HLS URL from API for camera ${camera.id}: ${authenticatedUrl}`);
        setHlsUrl(authenticatedUrl);
      } else if (isRtspStream) {
        // Legacy fallback: generate HLS URL from camera ID (already includes token)
        const hlsUrl = getHlsUrl();
        console.log(`🎥 Fallback HLS URL for camera ${camera.id}: ${hlsUrl}`);
        setHlsUrl(hlsUrl);
      } else {
        console.error('No valid stream URL available for camera', camera.id);
      }
    } catch (error) {
      console.error('Error setting up stream:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize HLS.js when hlsUrl changes
  useEffect(() => {
    if (hlsUrl && videoRef.current && Hls.isSupported()) {
      // Destroy previous HLS instance
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }

      const hls = new Hls({
        debug: false,
        enableWorker: true,
        lowLatencyMode: true,
        liveSyncDurationCount: 2,
        liveMaxLatencyDurationCount: 8,
        maxBufferLength: 20,
        maxMaxBufferLength: 40,
        maxBufferSize: 50 * 1000 * 1000, // 30MB
        maxBufferHole: 0.2,
        highBufferWatchdogPeriod: 2,
        nudgeOffset: 0.2,
        nudgeMaxRetry: 5,
        maxFragLookUpTolerance: 0.2,
        startLevel: -1,
        abrEwmaDefaultEstimate: 500000,
        abrBandWidthFactor: 0.95,
        abrBandWidthUpFactor: 0.7,
        abrMaxWithRealBitrate: true,
        backBufferLength: 30,
        maxSeekHole: 2,
        fragLoadingTimeOut: 20000,
        manifestLoadingTimeOut: 20000,
        levelLoadingTimeOut: 20000,
      });

      hls.loadSource(hlsUrl);
      hls.attachMedia(videoRef.current);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        console.log('HLS manifest parsed, video can play');
        videoRef.current.play();
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS error:', data);
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.error('Network error, trying to recover...');
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.error('Media error, trying to recover...');
              hls.recoverMediaError();
              break;
            default:
              console.error('Fatal error, destroying HLS instance');
              hls.destroy();
              break;
          }
        } else {
          // Non-fatal errors - try to recover
          if (data.details === 'bufferStalledError') {
            console.log('Buffer stalled, trying to recover...');
            hls.startLoad();
          }
        }
      });

      hlsRef.current = hls;
    } else if (hlsUrl && videoRef.current && videoRef.current.canPlayType('application/vnd.apple.mpegurl')) {
      // Native HLS support (Safari)
      videoRef.current.src = hlsUrl;
      videoRef.current.addEventListener('loadedmetadata', () => {
        videoRef.current.play();
      });
    }

    // Cleanup on unmount
    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
    };
  }, [hlsUrl]);

  // Check HLS availability when modal opens
  React.useEffect(() => {
    // Always check for HLS availability - either from API response or fallback
    checkHlsAvailability();
  }, [camera.id]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={onClose} />
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 relative">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Live View: {camera.name}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Removed tabs - now shows live stream directly */}
          
          <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
            {/* Always show web view - removed tabs */}
            {true && (
              // Web Player View
              <div className="w-full h-full relative">
                {isLoading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading live stream...</p>
                    </div>
                  </div>
                ) : hlsUrl ? (
                  <video
                    ref={videoRef}
                    controls
                    className="w-full h-full rounded-lg"
                    title={`Live stream: ${camera.name}`}
                    onLoadStart={() => console.log('Video loading started')}
                    onCanPlay={() => console.log('Video can play')}
                    onError={(e) => {
                      console.error('Video error:', e);
                      console.error('Video error details:', e.target.error);
                      console.error('Video networkState:', e.target.networkState);
                      console.error('Video readyState:', e.target.readyState);
                    }}
                  >
                    Your browser does not support HLS video.
                  </video>
                ) : isRtspStream ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <Camera className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600 mb-4">RTSP stream converting to web format...</p>
                      <button
                        onClick={checkHlsAvailability}
                        className="btn btn-primary"
                      >
                        Retry Connection
                      </button>
                    </div>
                  </div>
                ) : camera.stream_url ? (
                  <iframe
                    src={camera.stream_url}
                    className="w-full h-full rounded-lg"
                    title={`Live stream: ${camera.name}`}
                    allow="camera; microphone"
                  />
                ) : (
                  <div className="text-center">
                    <Camera className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Camera stream not available</p>
                  </div>
                )}
                {hlsUrl && (
                  <div className="absolute top-4 right-4">
                    <a
                      href={hlsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-black bg-opacity-50 text-white px-3 py-1 rounded text-sm flex items-center"
                    >
                      <ExternalLink className="h-4 w-4 mr-1" />
                      Open in new tab
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-4 text-sm text-gray-500">
            <p>Note: Live stream availability depends on camera configuration and network connectivity.</p>
            {isRtspStream && (
              <p className="mt-1">
                <strong>RTSP Tip:</strong> For the best viewing experience, use VLC Media Player or similar RTSP-capable software.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Camera Photos Modal
const CameraPhotosModal = ({ camera, onClose }) => {
  const [photos, setPhotos] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isIndexing, setIsIndexing] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [imageSrcById, setImageSrcById] = useState({});
  const [currentlyLoadingIndex, setCurrentlyLoadingIndex] = useState(-1);

  // SIMPLE: Load thumbnails one by one, in order
  const loadThumbnailsSequentially = useCallback((photos) => {
    if (!photos || photos.length === 0) return;
    
    console.log(`🔄 Starting to load ${photos.length} thumbnails sequentially`);
    setCurrentlyLoadingIndex(0);
    
    const loadNext = (index) => {
      if (index >= photos.length) {
        console.log(`✅ All thumbnails loaded`);
        setCurrentlyLoadingIndex(-1);
        return;
      }
      
      const photo = photos[index];
      console.log(`🔄 Loading thumbnail ${index + 1}/${photos.length} for photo ${photo.id}`);
      
      const thumbUrl = `/api/camera-photos/thumbnail?token=${localStorage.getItem('token')}&photoId=${photo.id}`;
      setImageSrcById(prev => ({ ...prev, [photo.id]: thumbUrl }));
      setCurrentlyLoadingIndex(index);
      
      const img = new window.Image();
      img.onload = () => {
        console.log(`✅ Thumbnail loaded for photo ${photo.id}`);
        setTimeout(() => loadNext(index + 1), 500); // Wait 500ms before next
      };
      img.onerror = () => {
        console.log(`❌ Thumbnail failed for photo ${photo.id}, moving to next`);
        setImageSrcById(prev => ({ ...prev, [photo.id]: '/placeholder-image.svg' }));
        setTimeout(() => loadNext(index + 1), 500); // Wait 500ms before next
      };
      img.src = thumbUrl;
    };
    
    loadNext(0);
  }, []);

  const loadPhotos = async (page = 1) => {
    setIsLoading(true);
    try {
      // Changed from 50 to 12 photos per page
      const response = await fetch(`/api/camera-photos/${camera.id}/photos?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}&limit=12&offset=${(page - 1) * 12}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPhotos(data.photos || []);
        setPagination(data.pagination || null);
        setCurrentPage(page);
        
        // Reset image sources for lazy loading
        setImageSrcById({});
      } else {
        toast.error('Failed to load photos');
      }
    } catch (error) {
      console.error('Error loading photos:', error);
      toast.error('Failed to load photos');
    } finally {
      setIsLoading(false);
    }
  };

  const indexPhotos = async () => {
    setIsIndexing(true);
    try {
      const response = await fetch(`/api/camera-photos/${camera.id}/index`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(dateRange)
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`Indexed ${data.indexedCount} photos! Thumbnails will be generated in the background.`);
        setCurrentPage(1); // Reset to first page
        loadPhotos(1); // Reload photos after indexing
      } else {
        toast.error('Failed to index photos');
      }
    } catch (error) {
      console.error('Error indexing photos:', error);
      toast.error('Failed to index photos');
    } finally {
      setIsIndexing(false);
    }
  };

  // Load thumbnails when photos change
  useEffect(() => {
    if (photos.length > 0) {
      console.log(`📸 Photos loaded, starting sequential thumbnail loading`);
      loadThumbnailsSequentially(photos);
    }
  }, [photos, loadThumbnailsSequentially]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when date range changes
    loadPhotos(1);
  }, [dateRange]);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={onClose} />
        <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-6 relative max-h-screen overflow-y-auto">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Photos: {camera.name}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Controls */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="space-y-2">
                {/* Index Photos button - HIDDEN: Could not get manual indexing to work reliably within timeout limits */}
                {false && (
                  <button
                    onClick={indexPhotos}
                    disabled={isIndexing}
                    className="w-full btn btn-primary flex items-center justify-center"
                  >
                    {isIndexing ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    {isIndexing ? 'Indexing...' : 'Index Photos'}
                  </button>
                )}
                <button
                  onClick={() => loadPhotos(currentPage)}
                  disabled={isLoading}
                  className="w-full btn btn-secondary flex items-center justify-center"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {/* Photo Grid */}
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {photos.map((photo, index) => (
                <div 
                  key={photo.id} 
                  className="bg-gray-100 rounded-lg overflow-hidden"
                >
                  <div className={`w-full h-32 bg-gray-200 flex items-center justify-center ${
                    imageSrcById[photo.id] ? 'hidden' : ''
                  }`}>
                    <div className="text-gray-500 text-sm">Loading...</div>
                  </div>
                  <img
                    src={imageSrcById[photo.id] || undefined}
                    alt={photo.filename}
                    className={`w-full h-32 object-cover cursor-pointer hover:opacity-75 transition-opacity duration-300 ${
                      imageSrcById[photo.id] ? 'opacity-0' : 'hidden'
                    }`}
                    loading="lazy"
                    onClick={() => window.open(`/photo?token=${localStorage.getItem('token')}&photoId=${photo.id}`, '_blank')}
                    onLoad={(e) => {
                      // Show image when loaded
                      e.target.classList.remove('opacity-0');
                      e.target.classList.add('opacity-100');
                    }}
                    onError={(e) => {
                      // If thumbnail fails, just show placeholder - don't try to load full photo
                      console.log(`❌ Thumbnail failed for photo ${photo.id}, showing placeholder`);
                      setImageSrcById((prev) => ({ ...prev, [photo.id]: '/placeholder-image.svg' }));
                    }}
                  />
                  <div className="p-2">
                    <p className="text-xs text-gray-600 truncate">{photo.filename}</p>
                    <p className="text-xs text-gray-500">
                      {(() => {
                        try {
                          // Check if timestamp already has timezone info
                          const timestamp = photo.timestamp;
                          const dateString = timestamp && !timestamp.endsWith('Z') ? timestamp + 'Z' : timestamp;
                          const dateObj = new Date(dateString);
                          // Create a new date using UTC values to force UTC formatting
                          const utcDate = new Date(dateObj.getUTCFullYear(), dateObj.getUTCMonth(), dateObj.getUTCDate(), 
                                                   dateObj.getUTCHours(), dateObj.getUTCMinutes(), dateObj.getUTCSeconds());
                          const utcFormatted = format(utcDate, 'MMM d, HH:mm');

                          return timestamp ? utcFormatted : 'No timestamp';
                        } catch (error) {
                          console.error('Date formatting error:', error, 'for timestamp:', photo.timestamp);
                          return 'Invalid date';
                        }
                      })()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Image className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No photos found</h3>
              <p className="text-gray-500 mb-4">
                No photos available for the selected date range. Try indexing photos from SFTP.
              </p>
              <button
                onClick={indexPhotos}
                disabled={isIndexing}
                className="btn btn-primary flex items-center mx-auto"
              >
                {isIndexing ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                {isIndexing ? 'Indexing...' : 'Index Photos from SFTP'}
              </button>
            </div>
          )}

          {/* Pagination Controls */}
          {pagination && pagination.total > 12 && (
            <div className="mt-6 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {((currentPage - 1) * 12) + 1} to {Math.min(currentPage * 12, pagination.total)} of {pagination.total} photos
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => loadPhotos(currentPage - 1)}
                  disabled={currentPage <= 1 || isLoading}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    currentPage <= 1 || isLoading
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </button>
                
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, Math.ceil(pagination.total / 12)) }, (_, i) => {
                    const totalPages = Math.ceil(pagination.total / 12);
                    let pageNum;
                    
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => loadPhotos(pageNum)}
                        disabled={isLoading}
                        className={`px-3 py-2 text-sm font-medium rounded-md ${
                          currentPage === pageNum
                            ? 'bg-blue-600 text-white'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        } ${isLoading ? 'cursor-not-allowed opacity-50' : ''}`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                </div>
                
                <button
                  onClick={() => loadPhotos(currentPage + 1)}
                  disabled={!pagination.hasMore || isLoading}
                  className={`px-3 py-2 text-sm font-medium rounded-md ${
                    !pagination.hasMore || isLoading
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Create Timelapse Modal

const CreateTimelapseModal = ({ camera, onClose }) => {
  const [isCreating, setIsCreating] = useState(false);
  const [imageCount, setImageCount] = useState(0);
  const [api] = useState(() => new TimelapseAPI());
  
  const [formData, setFormData] = useState({
    camera: camera.name, // Use the current camera directly
    startDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    fps: 30,
    quality: 'standard',
    name: `${camera.name} Timelapse`,
    sortOrder: 'asc'
  });

  // Check image count when date range changes
  useEffect(() => {
    if (formData.startDate && formData.endDate) {
      const checkImageCount = async () => {
        try {
          const imageData = await api.getImages(
            camera.name, 
            formData.startDate, 
            formData.endDate, 
            1 // Just get count, not actual images
          );
          setImageCount(imageData.totalImages);
        } catch (error) {
          console.error('Failed to get image count:', error);
          setImageCount(0);
        }
      };

      checkImageCount();
    }
  }, [formData.startDate, formData.endDate, camera.name, api]);

  const createTimelapse = async () => {
    setIsCreating(true);
    try {
      const response = await api.createTimelapse(formData);
      
      toast.success(`Timelapse job created! Job ID: ${response.jobId}`);
      
      // Start monitoring the job
      monitorJobProgress(response.jobId);
      
      onClose();
    } catch (error) {
      console.error('Error creating timelapse:', error);
      
      const errorMessage = handleAPIError(error);
      toast.error(errorMessage);
    } finally {
      setIsCreating(false);
    }
  };

  const monitorJobProgress = (jobId) => {
    const checkProgress = async () => {
      try {
        const job = await api.getJobStatus(jobId);
        
        if (job.status === 'completed') {
          toast.success('Timelapse completed! Ready for download.');
        } else if (job.status === 'failed') {
          toast.error(`Timelapse failed: ${job.error}`);
        } else if (job.status === 'processing') {
          // Continue monitoring
          setTimeout(checkProgress, 2000);
        }
        
      } catch (error) {
        console.error('Error monitoring job:', error);
      }
    };
    
    checkProgress();
  };

  const handleAPIError = (error) => {
    switch (true) {
      case error.message.includes('Network'):
        return 'Cannot connect to timelapse server. Please check your connection.';
      
      case error.message.includes('404'):
        return 'Timelapse service not found. Please contact support.';
      
      case error.message.includes('No images found'):
        return 'No images available for the selected date range.';
      
      case error.message.includes('FFmpeg failed'):
        return 'Video processing failed. Please try again with different settings.';
      
      default:
        return `Timelapse error: ${error.message}`;
    }
  };

  const qualityOptions = [
    { value: 'draft', label: 'Draft (720p, Fast)', description: 'Quick preview, low quality' },
    { value: 'standard', label: 'Standard (1080p)', description: 'Good quality, balanced speed' },
    { value: 'high', label: 'High (1440p)', description: 'High quality, slower processing' },
    { value: 'ultra', label: 'Ultra (4K)', description: 'Maximum quality, slowest processing' }
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75" onClick={onClose} />
        <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6 relative">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              Create Timelapse: {camera.name}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="space-y-4 max-h-96 overflow-y-auto">
            {/* Basic Settings */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Timelapse Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter timelapse name"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quality Level</label>
                <select
                  value={formData.quality}
                  onChange={(e) => setFormData(prev => ({ ...prev, quality: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {qualityOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Frame Rate (FPS)</label>
                <select
                  value={formData.fps}
                  onChange={(e) => setFormData(prev => ({ ...prev, fps: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={15}>15 FPS</option>
                  <option value={24}>24 FPS</option>
                  <option value={30}>30 FPS</option>
                  <option value={60}>60 FPS</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <select
                value={formData.sortOrder}
                onChange={(e) => setFormData(prev => ({ ...prev, sortOrder: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="asc">Front to Back (oldest first)</option>
                <option value="desc">Back to Front (newest first)</option>
              </select>
            </div>

            {/* Image Count Display */}
            {imageCount > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-700">
                  <strong>Available Images:</strong> {imageCount.toLocaleString()} images in selected date range
                </p>
              </div>
            )}

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                <strong>Camera:</strong> {camera.name}<br />
                <strong>Date Range:</strong> {formData.startDate} to {formData.endDate}<br />
                <strong>Quality:</strong> {qualityOptions.find(q => q.value === formData.quality)?.label}<br />
                <strong>Processing:</strong> Remote (on SFTP server)
              </p>
            </div>

            <div className="flex space-x-3 pt-4">
              <button
                onClick={onClose}
                className="flex-1 btn btn-secondary"
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                onClick={createTimelapse}
                disabled={isCreating || !formData.name.trim()}
                className="flex-1 btn btn-primary flex items-center justify-center"
              >
                {isCreating ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Video className="h-4 w-4 mr-2" />
                )}
                {isCreating ? 'Creating...' : 'Create Timelapse'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CamerasPage;
